"use client";

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import clsx from "clsx";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Percent,
  Wallet,
  Banknote,
  RefreshCw,
  Download,
  Printer,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  CircleDollarSign,
} from "lucide-react";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgToast } from "@/components/fg-toast";
import { api } from "@/lib/api";

// ── Types ─────────────────────────────────────────────────────

interface FinanceKpis {
  activeSubscribers: number;
  paidSubscribers: number;
  totalSubscribers: number;
  trialUsers: number;
  freeUsers: number;
  revenueUsd: number;
  mrrUsd: number;
  arrUsd: number;
  aiCostUsd: number;
  grossMargin: number;
  newPayingCustomers: number;
  churnedCustomers: number;
  churnRate: number;
  arpuUsd: number;
  ltvUsd: number;
  bookingsRevenueUsd: number;
  packRevenueUsd: number;
  newSignups: number;
  totalUsers: number;
  conversionRate: number;
  fromIso: string;
  toIso: string;
  refundsUsd: number;
  dodoConnected: boolean;
  revenuecatConnected: boolean;
}

interface MonthlyRevenue {
  month: string;
  subscriptionUsd: number;
  packUsd: number;
  bookingsUsd: number;
  totalUsd: number;
  aiCostUsd: number;
  netUsd: number;
  netMargin: number;
  activeSubscribers: number;
  newCustomers: number;
  churnedCustomers: number;
}

interface RevenueByTier {
  tierId: string;
  tierName: string;
  levelCode: string | null;
  priceCents: number;
  currency: string;
  subscriberCount: number;
  newSubscribers: number;
  mrrContributionUsd: number;
}

interface RevenueBySource {
  source: "dodo_web" | "revenuecat_mobile" | "manual_admin" | "unknown";
  totalUsd: number;
  paymentCount: number;
  sharePct: number;
}

interface TopCustomer {
  userId: string;
  email: string;
  fullName: string;
  tierName: string | null;
  totalSpentUsd: number;
  monthsActive: number;
  ltvUsd: number;
}

interface FunnelStage {
  stage: "signups" | "free_active" | "subscribed" | "paying_30d" | "renewed";
  count: number;
  conversionFromPrev: number;
}

interface CohortRow {
  cohortMonth: string;
  size: number;
  retention: number[];
}

interface TierCatalogRow {
  id: string;
  name: string;
  levelCode: string | null;
  priceCents: number;
  currency: string;
  aiCreditsPerMonth: number;
  isActive: boolean;
  subscriberCount: number;
}

interface TierDistribution {
  tierName: string;
  count: number;
  sharePct: number;
}

interface BookingsBreakdown {
  status: string;
  count: number;
  revenueUsd: number;
}

interface RefundRow {
  paymentId: string;
  amountUsd: number;
  reason: string | null;
  createdAt: string;
}

interface FinanceSummary {
  kpis: FinanceKpis;
  monthly: MonthlyRevenue[];
  byTier: RevenueByTier[];
  bySource: RevenueBySource[];
  topCustomers: TopCustomer[];
  funnel: FunnelStage[];
  cohorts: CohortRow[];
  tiers: TierCatalogRow[];
  tierDistribution: TierDistribution[];
  bookingsBreakdown: BookingsBreakdown[];
  refunds: RefundRow[];
  generatedAt: string;
}

// ── Constants ────────────────────────────────────────────────

const SOURCE_LABEL: Record<RevenueBySource["source"], string> = {
  dodo_web: "Dodo (Web)",
  revenuecat_mobile: "RevenueCat (Mobile)",
  manual_admin: "Manual / Admin",
  unknown: "Other",
};
const SOURCE_COLOR: Record<RevenueBySource["source"], string> = {
  dodo_web: "#FFD60A",
  revenuecat_mobile: "#0A84FF",
  manual_admin: "#8E8E93",
  unknown: "#FF453A",
};

const FUNNEL_LABEL: Record<FunnelStage["stage"], string> = {
  signups: "Sign-ups",
  free_active: "Free tier",
  subscribed: "Any sub",
  paying_30d: "Paid 30d",
  renewed: "Renewed",
};
const FUNNEL_COLOR: Record<FunnelStage["stage"], string> = {
  signups: "#0A84FF",
  free_active: "#5AC8FA",
  subscribed: "#34C759",
  paying_30d: "#FFD60A",
  renewed: "#FF9F0A",
};

const TIER_COLORS = [
  "#FFD60A",
  "#0A84FF",
  "#34C759",
  "#FF9F0A",
  "#AF52DE",
  "#5AC8FA",
  "#FF453A",
];

// ── Helpers ──────────────────────────────────────────────────

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usdFull = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

function retentionColor(value: number): string {
  if (value >= 80) return "bg-[#34C759]/30 text-[#34C759]";
  if (value >= 50) return "bg-[#FFD60A]/30 text-[#FFD60A]";
  if (value >= 25) return "bg-[#FF9F0A]/30 text-[#FF9F0A]";
  return "bg-[#FF453A]/20 text-[#FF453A]";
}

// ── KPI Card ─────────────────────────────────────────────────

function KpiCard({
  title,
  value,
  hint,
  icon: Icon,
  trend,
  index = 0,
  emphasis = false,
}: {
  title: string;
  value: string;
  hint: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  trend?: { value: number; positive: boolean };
  index?: number;
  emphasis?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
    >
      <FgCard
        className={clsx(
          "p-4 relative overflow-hidden",
          emphasis && "border-[#FFD60A]/30 bg-[#FFD60A]/[0.04]",
        )}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#8E8E93]">
            {title}
          </div>
          <div
            className={clsx(
              "flex h-7 w-7 items-center justify-center rounded-lg border",
              emphasis
                ? "bg-[#FFD60A]/15 border-[#FFD60A]/30 text-[#FFD60A]"
                : "bg-white/5 border-white/10 text-[#FAFAFA]",
            )}
          >
            <Icon size={13} />
          </div>
        </div>
        <div className="text-2xl font-extrabold text-[#FAFAFA] tracking-tight">
          {value}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[10px] font-mono">
          {trend && (
            <span
              className={clsx(
                "inline-flex items-center gap-0.5 px-1 rounded",
                trend.positive
                  ? "text-[#34C759] bg-[#34C759]/10"
                  : "text-[#FF453A] bg-[#FF453A]/10",
              )}
            >
              {trend.positive ? <ArrowUpRight size={9} /> : <ArrowDownRight size={9} />}
              {Math.abs(trend.value).toFixed(1)}%
            </span>
          )}
          <span className="text-[#8E8E93]">{hint}</span>
        </div>
      </FgCard>
    </motion.div>
  );
}

// ── Section Card ─────────────────────────────────────────────

function Section({
  title,
  subtitle,
  children,
  rightSlot,
  className,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
  className?: string;
}) {
  return (
    <FgCard className={clsx("p-5", className)}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-[#FAFAFA]">{title}</h2>
          {subtitle && (
            <p className="text-xs text-[#8E8E93] mt-0.5">{subtitle}</p>
          )}
        </div>
        {rightSlot}
      </div>
      {children}
    </FgCard>
  );
}

// ── Custom Tooltip ───────────────────────────────────────────

function ChartTooltip({ active, payload, label, formatter }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  formatter?: (v: number) => string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 rounded-lg p-2.5 shadow-2xl text-xs">
      {label && (
        <div className="font-mono text-[10px] text-[#8E8E93] mb-1.5">{label}</div>
      )}
      {payload.map((p, idx) => (
        <div key={idx} className="flex items-center gap-2 py-0.5">
          <div className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#8E8E93] capitalize">{p.name}:</span>
          <span className="font-bold text-[#FAFAFA]">
            {formatter ? formatter(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────

export function FinancePage() {
  const [days, setDays] = useState(30);
  const [months] = useState(12);
  const [printing, setPrinting] = useState(false);

  const summary = useQuery<FinanceSummary>({
    queryKey: ["admin", "finance", "summary", days, months],
    queryFn: async () =>
      (await api.get(`/api/admin/finance/summary?days=${days}&months=${months}`)).data,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const data = summary.data;

  const kpiCards = useMemo(() => {
    if (!data) return [];
    const k = data.kpis;
    return [
      {
        title: "MRR",
        value: usdFull(k.mrrUsd),
        hint: `Period revenue: ${usdFull(k.revenueUsd)}`,
        icon: CircleDollarSign,
        emphasis: true,
      },
      {
        title: "ARR",
        value: usd(k.arrUsd),
        hint: `MRR × 12`,
        icon: TrendingUp,
      },
      {
        title: "Active Subs",
        value: k.paidSubscribers.toLocaleString(),
        hint: `${k.freeUsers} on Free tier`,
        icon: Users,
      },
      {
        title: "ARPU",
        value: usdFull(k.arpuUsd),
        hint: `LTV est. ${usdFull(k.ltvUsd)}`,
        icon: Wallet,
      },
      {
        title: "Churn",
        value: pct(k.churnRate),
        hint: `${k.churnedCustomers} churned in ${days}d`,
        icon: TrendingDown,
      },
      {
        title: "Gross Margin",
        value: pct(k.grossMargin),
        hint: `AI cost: ${usdFull(k.aiCostUsd)}`,
        icon: Percent,
      },
    ];
  }, [data, days]);

  const printPage = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrinting(false), 500);
    }, 100);
  };

  const exportCsv = () => {
    if (!data) return;
    const rows: string[][] = [
      ["Month", "Subscription USD", "Bookings USD", "AI Cost USD", "Net USD", "Active Subs", "New", "Churned"],
      ...data.monthly.map((m) => [
        m.month,
        m.subscriptionUsd.toFixed(2),
        m.bookingsUsd.toFixed(2),
        m.aiCostUsd.toFixed(2),
        m.netUsd.toFixed(2),
        String(m.activeSubscribers),
        String(m.newCustomers),
        String(m.churnedCustomers),
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fullgerman-finance-${data.generatedAt.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    FgToast.success("CSV exported");
  };

  return (
    <AdminShell>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8 no-print"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Banknote className="w-5 h-5 text-[#34C759]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#34C759]">
              FINANCIAL OVERVIEW
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            Revenue & Subscriptions
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            Investor-grade financial summary. Pulls from Dodo Payments, RevenueCat, and the local database.
          </p>
          {data && (
            <p className="mt-1 text-[10px] font-mono text-[#8E8E93]">
              Dodo: <span className={data.kpis.dodoConnected ? "text-[#34C759]" : "text-[#FF9F0A]"}>
                {data.kpis.dodoConnected ? "connected" : "not configured"}
              </span>{" "}
              · RevenueCat: <span className={data.kpis.revenuecatConnected ? "text-[#34C759]" : "text-[#FF9F0A]"}>
                {data.kpis.revenuecatConnected ? "connected" : "not configured"}
              </span>{" "}
              · Generated {new Date(data.generatedAt).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] p-0.5">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={clsx(
                  "px-2.5 py-1 text-xs font-bold rounded-md transition",
                  days === d
                    ? "bg-[#FFD60A] text-[#0A0A0A]"
                    : "text-[#8E8E93] hover:text-[#FAFAFA]",
                )}
              >
                {d}d
              </button>
            ))}
          </div>
          <FgButton
            size="sm"
            variant="secondary"
            onClick={() => summary.refetch()}
            leftIcon={
              <RefreshCw className={clsx("w-3.5 h-3.5", summary.isFetching && "animate-spin")} />
            }
          >
            Refresh
          </FgButton>
          <FgButton
            size="sm"
            variant="ghost"
            onClick={exportCsv}
            leftIcon={<Download className="w-3.5 h-3.5" />}
          >
            CSV
          </FgButton>
          <FgButton
            size="sm"
            onClick={printPage}
            leftIcon={<Printer className="w-3.5 h-3.5" />}
          >
            Print
          </FgButton>
        </div>
      </motion.div>

      {/* Print header — only visible when printing */}
      {printing && data && (
        <div className="print-only mb-6">
          <h1 className="text-2xl font-extrabold text-[#0A0A0A]">
            FullGerman — Investor Summary
          </h1>
          <p className="text-xs text-[#8E8E93] mt-1">
            Generated {new Date(data.generatedAt).toLocaleString()}
          </p>
        </div>
      )}

      {summary.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 no-print">
          {Array.from({ length: 6 }).map((_, i) => (
            <FgCard key={i} className="p-4 h-28 fg-skeleton" />
          ))}
        </div>
      ) : !data ? (
        <FgCard className="p-12 text-center no-print">
          <AlertCircle className="w-12 h-12 mx-auto text-[#FF9F0A] mb-3" />
          <h3 className="text-lg font-bold text-[#FAFAFA]">Could not load finance data</h3>
          <p className="mt-1 text-sm text-[#8E8E93]">
            {(summary.error as { message?: string })?.message ?? "Try refreshing."}
          </p>
        </FgCard>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 no-print">
            {kpiCards.map((k, i) => (
              <KpiCard key={k.title} {...k} index={i} />
            ))}
          </div>

          {/* Revenue chart */}
          <Section
            title="Revenue (last 12 months)"
            subtitle="Subscription + bookings revenue, AI costs, and net margin"
            rightSlot={
              <div className="flex items-center gap-2 text-[10px] font-mono text-[#8E8E93]">
                <div className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#34C759]"/> Sub</div>
                <div className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#0A84FF]"/> Bookings</div>
                <div className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#FF453A]"/> AI cost</div>
              </div>
            }
          >
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.monthly}>
                  <defs>
                    <linearGradient id="g-sub" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34C759" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#34C759" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="g-book" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#0A84FF" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="g-ai" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF453A" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#FF453A" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" stroke="#8E8E93" style={{ fontSize: 10, fontFamily: "monospace" }} />
                  <YAxis stroke="#8E8E93" style={{ fontSize: 10, fontFamily: "monospace" }} />
                  <Tooltip content={<ChartTooltip formatter={usdFull} />} />
                  <Area type="monotone" dataKey="subscriptionUsd" name="Subscription" stroke="#34C759" fill="url(#g-sub)" />
                  <Area type="monotone" dataKey="bookingsUsd" name="Bookings" stroke="#0A84FF" fill="url(#g-book)" />
                  <Area type="monotone" dataKey="aiCostUsd" name="AI cost" stroke="#FF453A" fill="url(#g-ai)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Section>

          {/* Net margin trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Section title="Net margin" subtitle="(revenue − AI cost) ÷ revenue">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="month" stroke="#8E8E93" style={{ fontSize: 10, fontFamily: "monospace" }} />
                    <YAxis stroke="#8E8E93" style={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                    <Tooltip content={<ChartTooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} />} />
                    <Line
                      type="monotone"
                      dataKey="netMargin"
                      name="Net margin"
                      stroke="#FFD60A"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#FFD60A" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Subscribers trend" subtitle="Active subscribers + net new per month">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="month" stroke="#8E8E93" style={{ fontSize: 10, fontFamily: "monospace" }} />
                    <YAxis stroke="#8E8E93" style={{ fontSize: 10, fontFamily: "monospace" }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="newCustomers" name="New" fill="#34C759" stackId="a" />
                    <Bar dataKey="churnedCustomers" name="Churned" fill="#FF453A" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>

          {/* Revenue by tier + by source */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Section title="Revenue by tier" subtitle="MRR contribution per subscription tier">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.byTier.filter((t) => t.subscriberCount > 0)}
                        dataKey="mrrContributionUsd"
                        nameKey="tierName"
                        innerRadius={45}
                        outerRadius={75}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {data.byTier.map((t, i) => (
                          <Cell
                            key={t.tierId}
                            fill={TIER_COLORS[i % TIER_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip formatter={usdFull} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5">
                  {data.byTier.map((t, i) => (
                    <div key={t.tierId} className="flex items-center gap-2 text-xs">
                      <div
                        className="h-2.5 w-2.5 rounded-sm shrink-0"
                        style={{ background: TIER_COLORS[i % TIER_COLORS.length] }}
                      />
                      <span className="text-[#FAFAFA] font-semibold flex-1">{t.tierName}</span>
                      <span className="text-[#8E8E93] font-mono">{t.subscriberCount}</span>
                      <span className="text-[#FAFAFA] font-mono w-16 text-right">
                        {usdFull(t.mrrContributionUsd)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            <Section title="Revenue by source" subtitle="Dodo vs RevenueCat vs manual">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.bySource}
                        dataKey="totalUsd"
                        nameKey="source"
                        innerRadius={45}
                        outerRadius={75}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {data.bySource.map((s) => (
                          <Cell key={s.source} fill={SOURCE_COLOR[s.source]} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={
                          <ChartTooltip
                            formatter={(v) => usdFull(v)}
                          />
                        }
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5">
                  {data.bySource.map((s) => (
                    <div key={s.source} className="flex items-center gap-2 text-xs">
                      <div
                        className="h-2.5 w-2.5 rounded-sm shrink-0"
                        style={{ background: SOURCE_COLOR[s.source] }}
                      />
                      <span className="text-[#FAFAFA] font-semibold flex-1">
                        {SOURCE_LABEL[s.source]}
                      </span>
                      <span className="text-[#8E8E93] font-mono">{s.paymentCount} tx</span>
                      <span className="text-[#FAFAFA] font-mono w-16 text-right">
                        {usdFull(s.totalUsd)}
                      </span>
                    </div>
                  ))}
                  {data.bySource.every((s) => s.paymentCount === 0) && (
                    <p className="text-xs text-[#8E8E93] mt-2">
                      No transactions in this window.
                    </p>
                  )}
                </div>
              </div>
            </Section>
          </div>

          {/* Top customers */}
          <Section
            title="Top customers (by spend)"
            subtitle={`Top ${data.topCustomers.length} paying customers`}
            className="mt-4"
          >
            {data.topCustomers.length === 0 ? (
              <p className="text-sm text-[#8E8E93] text-center py-8">No paying customers yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase text-[#8E8E93] border-b border-white/5">
                      <th className="py-2 pr-2">Customer</th>
                      <th className="py-2 px-2">Tier</th>
                      <th className="py-2 px-2 text-right">Months</th>
                      <th className="py-2 px-2 text-right">Total spent</th>
                      <th className="py-2 pl-2 text-right">LTV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topCustomers.map((c, idx) => (
                      <tr
                        key={c.userId}
                        className={clsx(
                          "border-b border-white/[0.04] hover:bg-white/[0.02] transition",
                          idx % 2 === 1 && "bg-white/[0.01]",
                        )}
                      >
                        <td className="py-2 pr-2">
                          <div className="text-[#FAFAFA] font-semibold">{c.fullName}</div>
                          <div className="text-[10px] font-mono text-[#8E8E93]">{c.email}</div>
                        </td>
                        <td className="py-2 px-2">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#FFD60A]/15 text-[#FFD60A] border border-[#FFD60A]/30">
                            {c.tierName ?? "—"}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-[#FAFAFA]">{c.monthsActive}</td>
                        <td className="py-2 px-2 text-right font-mono text-[#FAFAFA]">
                          {usdFull(c.totalSpentUsd)}
                        </td>
                        <td className="py-2 pl-2 text-right font-mono text-[#34C759]">
                          {usdFull(c.ltvUsd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Funnel */}
          <Section
            title="Acquisition funnel"
            subtitle="User journey from signup to paying customer"
            className="mt-4"
          >
            <div className="space-y-2">
              {data.funnel.map((s, i) => {
                const max = data.funnel[0]?.count || 1;
                const w = Math.max(2, Math.round((s.count / max) * 100));
                return (
                  <div key={s.stage} className="flex items-center gap-3">
                    <div className="w-32 text-xs text-[#FAFAFA] font-semibold">
                      {FUNNEL_LABEL[s.stage]}
                    </div>
                    <div className="flex-1 h-7 bg-white/[0.03] rounded-md overflow-hidden relative">
                      <div
                        className="h-full rounded-md flex items-center px-2 transition-all"
                        style={{
                          width: `${w}%`,
                          background: FUNNEL_COLOR[s.stage] + "30",
                          borderLeft: `3px solid ${FUNNEL_COLOR[s.stage]}`,
                        }}
                      >
                        <span className="text-[11px] font-mono font-bold text-[#FAFAFA]">
                          {s.count}
                        </span>
                      </div>
                    </div>
                    <div className="w-24 text-right text-[10px] font-mono text-[#8E8E93]">
                      {i === 0 ? "" : `${(s.conversionFromPrev * 100).toFixed(1)}% conv`}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Cohort retention heatmap */}
          <Section
            title="Cohort retention"
            subtitle="Monthly signup cohorts × months since signup (% still active)"
            className="mt-4"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase text-[#8E8E93] border-b border-white/5">
                    <th className="py-2 pr-2">Cohort</th>
                    <th className="py-2 px-2 text-right">Size</th>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <th key={i} className="py-2 px-1.5 text-center">M{i}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.cohorts.map((c) => (
                    <tr key={c.cohortMonth} className="border-b border-white/[0.04]">
                      <td className="py-2 pr-2 font-mono text-[#FAFAFA] font-semibold">
                        {c.cohortMonth}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-[#FAFAFA]">
                        {c.size}
                      </td>
                      {Array.from({ length: 6 }).map((_, i) => {
                        const r = c.retention[i];
                        return (
                          <td key={i} className="py-1.5 px-1.5 text-center">
                            {r != null ? (
                              <div
                                className={clsx(
                                  "inline-block min-w-[36px] py-1 rounded font-mono font-bold text-[10px]",
                                  retentionColor(r),
                                )}
                              >
                                {r.toFixed(0)}%
                              </div>
                            ) : (
                              <span className="text-[#8E8E93]">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Tier catalog + Bookings breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Section title="Tier catalog" subtitle="Live pricing + active subscribers">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase text-[#8E8E93] border-b border-white/5">
                      <th className="py-2 pr-2">Tier</th>
                      <th className="py-2 px-2 text-right">Price</th>
                      <th className="py-2 px-2 text-right">Credits/mo</th>
                      <th className="py-2 pl-2 text-right">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tiers.map((t) => (
                      <tr key={t.id} className="border-b border-white/[0.04]">
                        <td className="py-2 pr-2">
                          <div className="text-[#FAFAFA] font-semibold">{t.name}</div>
                          {t.levelCode && (
                            <div className="text-[10px] font-mono text-[#8E8E93]">
                              {t.levelCode}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-[#FAFAFA]">
                          {t.priceCents === 0 ? "Free" : usd(t.priceCents / 100)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-[#FFD60A]">
                          {t.aiCreditsPerMonth}
                        </td>
                        <td className="py-2 pl-2 text-right font-mono">
                          <span
                            className={clsx(
                              "inline-block min-w-[28px] text-center px-1.5 py-0.5 rounded font-bold text-[10px]",
                              t.subscriberCount > 0
                                ? "bg-[#34C759]/15 text-[#34C759]"
                                : "bg-white/5 text-[#8E8E93]",
                            )}
                          >
                            {t.subscriberCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Bookings breakdown" subtitle="1-on-1 tutor session revenue">
              {data.bookingsBreakdown.length === 0 ? (
                <p className="text-sm text-[#8E8E93] text-center py-8">
                  No bookings in the selected window.
                </p>
              ) : (
                <div className="space-y-2">
                  {data.bookingsBreakdown.map((b) => {
                    const total = data.bookingsBreakdown.reduce(
                      (s, x) => s + x.revenueUsd,
                      0,
                    );
                    const share = total > 0 ? (b.revenueUsd / total) * 100 : 0;
                    const tone =
                      b.status === "confirmed"
                        ? "bg-[#34C759]"
                        : b.status === "pending"
                        ? "bg-[#FFD60A]"
                        : b.status === "cancelled"
                        ? "bg-[#FF453A]"
                        : b.status === "completed"
                        ? "bg-[#0A84FF]"
                        : "bg-[#8E8E93]";
                    return (
                      <div key={b.status} className="flex items-center gap-2">
                        <div className={clsx("h-2.5 w-2.5 rounded-sm shrink-0", tone)} />
                        <div className="text-xs text-[#FAFAFA] font-semibold flex-1 capitalize">
                          {b.status}
                        </div>
                        <div className="text-[10px] font-mono text-[#8E8E93]">
                          {b.count} bookings
                        </div>
                        <div className="text-xs font-mono text-[#FAFAFA] w-20 text-right">
                          {usdFull(b.revenueUsd)}
                        </div>
                        <div className="text-[10px] font-mono text-[#8E8E93] w-12 text-right">
                          {share.toFixed(0)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          </div>

          {/* Refunds */}
          <Section
            title="Refunds"
            subtitle={
              data.refunds.length === 0
                ? "No refunds yet"
                : `From Dodo Payments (${data.refunds.length} most recent)`
            }
            className="mt-4"
          >
            {data.refunds.length === 0 ? (
              <p className="text-sm text-[#8E8E93] text-center py-8">
                {data.kpis.dodoConnected
                  ? "No refunds recorded."
                  : "Connect Dodo Payments to view refunds."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase text-[#8E8E93] border-b border-white/5">
                      <th className="py-2 pr-2">Date</th>
                      <th className="py-2 px-2">Payment ID</th>
                      <th className="py-2 px-2">Reason</th>
                      <th className="py-2 pl-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.refunds.map((r, idx) => (
                      <tr key={r.paymentId + idx} className="border-b border-white/[0.04]">
                        <td className="py-2 pr-2 font-mono text-[#FAFAFA]">
                          {new Date(r.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-2 px-2 font-mono text-[#8E8E93]">
                          {r.paymentId}
                        </td>
                        <td className="py-2 px-2 text-[#FAFAFA]">
                          {r.reason ?? "—"}
                        </td>
                        <td className="py-2 pl-2 text-right font-mono text-[#FF453A]">
                          {usdFull(r.amountUsd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Footer for print */}
          {printing && (
            <div className="print-only mt-12 pt-6 border-t border-[#0A0A0A]/20 text-[10px] text-[#8E8E93]">
              FullGerman investor summary — confidential. {new Date().toLocaleString()}
            </div>
          )}
        </>
      )}
    </AdminShell>
  );
}
